/**
 * Der Kalender auf dem Gerät: eigene Termine, Termine des Haushalts,
 * eingelesene aus Apple/Google und die Trainingstermine — in einer Liste.
 *
 * Gelesen wird **flach** in einem Schritt (Live-Abfragen, CLAUDE.md). Was die
 * Ansicht braucht — Zeitzone, Haushalt, eigenes Konto —, wird hineingereicht.
 */

import {
  isActivity,
  isoDateInZone,
  newId,
  stableId,
  trainingOccurrences,
  wallTimeInZone,
  eventActivityCountsFor,
} from '@kraftwerk/shared';
import type { Activity, DayExercise } from '@kraftwerk/shared';
import { db } from './local.js';
import type {
  EventFields,
  KraftwerkDatabase,
  LocalEvent,
  LocalEventMark,
  LocalFeedEvent,
  LocalHouseholdEvent,
} from './local.js';
import { stamp, writeRow, writeRows } from './write.js';
import type { RowChange } from './write.js';

export type AgendaKind = 'event' | 'household' | 'feed' | 'training' | 'task';

export interface AgendaItem {
  /** Eindeutig über alle Arten — für Listen-Schlüssel. */
  key: string;
  kind: AgendaKind;
  id: string;
  title: string;
  date: string;
  endDate: string | null;
  /** Ortszeit `HH:MM`, `null` = ganztägig. */
  time: string | null;
  durationMin: number | null;
  location: string | null;
  note: string | null;
  activity: Activity | null;
  activityMin: number | null;
  cancelled: boolean;
  /** Haushaltstermin: von mir angelegt? Nur dann zählt seine Aktivität für mich. */
  mine: boolean;
  householdId: string | null;
  /** Eingelesen: aus welchem Kalender. */
  feedId: string | null;
  /** Für wen (E16) — nur bei Haushaltsterminen, sonst leer. */
  forUserIds: string[];
}

function fromOwn(
  row: LocalEvent | LocalHouseholdEvent,
  kind: 'event' | 'household',
  me: string | null,
): AgendaItem {
  const householdId = kind === 'household' ? (row as LocalHouseholdEvent).householdId : null;
  return {
    key: `${kind}-${row.id}`,
    kind,
    id: row.id,
    title: row.title,
    date: row.date,
    endDate: row.endDate,
    time: row.time,
    durationMin: row.durationMin,
    location: row.location,
    note: row.note,
    activity: isActivity(row.activity) ? row.activity : null,
    activityMin: row.activityMin,
    cancelled: row.cancelled,
    mine: eventActivityCountsFor({
      kind,
      creatorId: row.userId,
      me,
      forUserIds: kind === 'household' ? (row as LocalHouseholdEvent).forUserIds : null,
    }),
    householdId,
    feedId: null,
    forUserIds: kind === 'household' ? ((row as LocalHouseholdEvent).forUserIds ?? []) : [],
  };
}

function fromFeed(
  row: LocalFeedEvent,
  mark: LocalEventMark | undefined,
  timeZone: string,
): AgendaItem {
  const minutes = row.endsAt
    ? Math.max(0, Math.round((Date.parse(row.endsAt) - Date.parse(row.startsAt)) / 60_000))
    : null;
  return {
    key: `feed-${row.id}`,
    kind: 'feed',
    id: row.id,
    title: row.title,
    date: row.date,
    endDate: row.endDate,
    time: row.allDay ? null : wallTimeInZone(new Date(row.startsAt), timeZone),
    durationMin: minutes,
    location: row.location,
    note: null,
    activity: mark && isActivity(mark.activity) ? mark.activity : null,
    activityMin: mark?.activityMin ?? null,
    cancelled: mark?.cancelled ?? false,
    mine: true,
    householdId: null,
    feedId: row.feedId,
    forUserIds: [],
  };
}

/** Überschneidet sich ein (ggf. mehrtägiger) Termin mit dem Fenster? */
function overlaps(
  item: { date: string; endDate: string | null },
  from: string,
  to: string,
): boolean {
  return item.date <= to && (item.endDate ?? item.date) >= from;
}

/**
 * Alle Einträge von `from` bis `to`, nach Tag und Uhrzeit (ganztägige zuerst).
 * Abgesagtes bleibt drin — die Ansicht streicht es durch.
 */
export async function listAgenda(
  from: string,
  to: string,
  context: { timeZone: string; me: string | null; trainingTitle: string },
  database: KraftwerkDatabase = db,
): Promise<AgendaItem[]> {
  const [own, shared, feed, marks, slots, days, taskRows, taskLists] = await Promise.all([
    database.events.toArray(),
    database.household_events.toArray(),
    database.feed_events.toArray(),
    database.event_marks.toArray(),
    database.training_slots.toArray(),
    database.training_days.toArray(),
    database.tasks.toArray(),
    database.task_lists.toArray(),
  ]);
  const listOf = new Map(
    taskLists.filter((list) => !list.deletedAt).map((list) => [list.id, list]),
  );
  const markOf = new Map(
    marks.filter((mark) => !mark.deletedAt).map((mark) => [mark.feedEventId, mark]),
  );

  const items: AgendaItem[] = [
    ...own
      .filter((row) => !row.deletedAt && overlaps(row, from, to))
      .map((row) => fromOwn(row, 'event', context.me)),
    ...shared
      .filter((row) => !row.deletedAt && overlaps(row, from, to))
      .map((row) => fromOwn(row, 'household', context.me)),
    ...feed
      .filter((row) => !row.deletedAt && overlaps(row, from, to))
      .map((row) => fromFeed(row, markOf.get(row.id), context.timeZone)),
    ...trainingOccurrences(
      slots.filter((row) => !row.deletedAt),
      days.filter((row) => !row.deletedAt),
      from,
      to,
      context.timeZone,
    ).map((entry): AgendaItem => ({
      key: `training-${entry.slotId ?? entry.dayId}-${entry.originDate ?? entry.date}`,
      kind: 'training',
      id: entry.dayId ?? `${entry.slotId}|${entry.originDate}`,
      title: context.trainingTitle,
      date: entry.date,
      endDate: null,
      time: entry.time,
      durationMin: entry.durationMin,
      location: null,
      note: null,
      // Training rechnet die Bilanz selbst (energyOfDay) — hier nicht noch einmal.
      activity: null,
      activityMin: null,
      cancelled: entry.status === 'cancelled',
      mine: true,
      householdId: null,
      feedId: null,
      forUserIds: [],
    })),
  ];

  // Offene Aufgaben mit Tag, die mich betreffen (mir zugewiesen, oder meine
  // ohne Zuständigen) — gleiche Regel wie `datedTasks`.
  for (const task of taskRows) {
    const list = listOf.get(task.listId);
    if (task.deletedAt || task.doneAt || !task.dueDate || !list || list.kind === 'checklist')
      continue;
    if (task.dueDate < from || task.dueDate > to) continue;
    const mine = task.assigneeId
      ? task.assigneeId === context.me
      : task.userId === undefined || task.userId === context.me;
    if (!mine) continue;
    items.push({
      key: `task-${task.id}`,
      kind: 'task',
      id: task.id,
      title: task.title,
      date: task.dueDate,
      endDate: null,
      time: task.dueTime,
      durationMin: null,
      location: null,
      note: list.name,
      activity: null,
      activityMin: null,
      cancelled: false,
      mine: true,
      householdId: task.householdId,
      feedId: null,
      forUserIds: [],
    });
  }

  return items.sort(
    (a, b) =>
      a.date.localeCompare(b.date) ||
      (a.time ?? '').localeCompare(b.time ?? '') ||
      a.title.localeCompare(b.title),
  );
}

/**
 * Sport aus dem Kalender für die Bilanz eines Tages.
 *
 * Zählt: eigene Termine und eigene Haushaltstermine mit Aktivität, markierte
 * eingelesene — nicht abgesagt. Vorbei heißt erledigt, sonst erwartet.
 */
export function calendarExercises(
  items: readonly AgendaItem[],
  date: string,
  timeZone: string,
  now: Date,
): DayExercise[] {
  const today = wallTimeInZone(now, timeZone);
  const isoToday = isoDateInZone(now, timeZone);
  const result: DayExercise[] = [];
  for (const item of items) {
    if (item.date !== date || item.cancelled || !item.mine || !item.activity) continue;
    const minutes = item.activityMin ?? item.durationMin ?? 0;
    if (minutes <= 0) continue;
    let done = date < isoToday;
    if (date === isoToday && item.time) {
      const [h, m] = item.time.split(':').map(Number);
      const end = h! * 60 + m! + minutes;
      const [nh, nm] = today.split(':').map(Number);
      done = nh! * 60 + nm! >= end;
    }
    result.push({ activity: item.activity, minutes, state: done ? 'done' : 'planned' });
  }
  return result;
}

// --- Schreiben ---------------------------------------------------------------

export interface EventDraft extends EventFields {
  /** Im Haushalt teilen — braucht einen Haushalt. */
  shared: boolean;
  /** Für wen (E16) — nur bei geteilten Terminen. */
  forUserIds?: string[];
}

/**
 * Termin anlegen oder ändern. Wechselt er zwischen privat und geteilt, zieht er
 * um: neue Zeile in der anderen Tabelle, die alte weich gelöscht — in einem
 * Schritt, damit er nie doppelt oder gar nicht dasteht.
 */
export async function saveEvent(
  draft: EventDraft,
  existing: { kind: 'event' | 'household'; id: string } | null,
  householdId: string | null,
  database: KraftwerkDatabase = db,
): Promise<{ kind: 'event' | 'household'; id: string }> {
  const now = stamp();
  const fields: EventFields = {
    title: draft.title.trim(),
    date: draft.date,
    time: draft.time,
    durationMin: draft.durationMin,
    endDate: draft.endDate && draft.endDate > draft.date ? draft.endDate : null,
    location: draft.location?.trim() || null,
    note: draft.note?.trim() || null,
    activity: draft.activity,
    activityMin: draft.activity ? draft.activityMin : null,
    cancelled: draft.cancelled,
  };
  const targetKind: 'event' | 'household' = draft.shared && householdId ? 'household' : 'event';
  const sameTable = existing?.kind === targetKind;
  const id = sameTable && existing ? existing.id : newId();

  const changes: RowChange[] = [];
  if (targetKind === 'household') {
    const before =
      sameTable && existing ? await database.household_events.get(existing.id) : undefined;
    changes.push({
      table: 'household_events',
      row: {
        ...before,
        ...fields,
        id,
        householdId: householdId!,
        forUserIds: draft.forUserIds ?? [],
        updatedAt: now,
        deletedAt: null,
      },
    });
  } else {
    const before = sameTable && existing ? await database.events.get(existing.id) : undefined;
    changes.push({
      table: 'events',
      row: { ...before, ...fields, id, updatedAt: now, deletedAt: null },
    });
  }
  if (existing && !sameTable) {
    const table = existing.kind === 'household' ? 'household_events' : 'events';
    const old =
      existing.kind === 'household'
        ? await database.household_events.get(existing.id)
        : await database.events.get(existing.id);
    if (old)
      changes.push({ table, row: { ...old, updatedAt: now, deletedAt: now }, kind: 'delete' });
  }
  await writeRows(changes, database);
  return { kind: targetKind, id };
}

export async function deleteEvent(
  kind: 'event' | 'household',
  id: string,
  database: KraftwerkDatabase = db,
): Promise<void> {
  const now = stamp();
  if (kind === 'household') {
    const row = await database.household_events.get(id);
    if (row && !row.deletedAt) {
      await writeRow(
        'household_events',
        { ...row, updatedAt: now, deletedAt: now },
        'delete',
        database,
      );
    }
    return;
  }
  const row = await database.events.get(id);
  if (row && !row.deletedAt) {
    await writeRow('events', { ...row, updatedAt: now, deletedAt: now }, 'delete', database);
  }
}

/** Eingelesenen Termin als Aktivität markieren oder als ausgefallen — feste Kennung je Termin. */
export async function markFeedEvent(
  feedEventId: string,
  mark: { activity: Activity | null; activityMin: number | null; cancelled: boolean },
  database: KraftwerkDatabase = db,
): Promise<void> {
  const id = stableId(`mark|${feedEventId}`);
  const before = await database.event_marks.get(id);
  await writeRow(
    'event_marks',
    {
      ...before,
      id,
      feedEventId,
      activity: mark.activity,
      activityMin: mark.activity ? mark.activityMin : null,
      cancelled: mark.cancelled,
      updatedAt: stamp(),
      deletedAt: null,
    },
    'upsert',
    database,
  );
}
